import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";

interface Persona {
    name: string;
    image: string;
    badgeText: string;
    badgeClass: string;
    badgeTextClass: string;
    roleColorClass: string;
    description: string;
    age: string;
    location: string;
    income: string;
    profession: string;
    experience: string;
    psychologicalTraits: string;
    educationalBackground: string;
    expertiseTags: string[];
}

// Data for personas
const personas: Persona[] = [
    {
        name: "Dr. Sarah Chen",
        image: "/1.jpeg",
        badgeText: "AI",
        badgeClass: "bg-purple-300",
        badgeTextClass: "text-purple-900",
        roleColorClass: "bg-blue-500",
        description:
            "I'm a board-certified endocrinologist with 12 years of experience in the pharmaceutical industry, currently serving as Senior Medical Director at a leading pharma co. I specialize in diabetes and cardiovascular portfolios, having previously worked as a clinical practitioner for 6 years. I review both promotional and scientific materials, including clinical trial protocols, investigator brochures, and medical communications. I've reviewed over 2000 materials across multiple brands and currently lead a team of medical reviewers. My expertise spans from reviewing complex publications to ensuring promotional claims are medically accurate and scientifically sound.",
        age: "48",
        location: "Gaithersburg, MD",
        income: "$320,000",
        profession: "Senior Medical Director",
        experience: "18 years (6 clinical, 12 pharma)",
        educationalBackground:
            "MD from Johns Hopkins University, Clinical Fellowship in Endocrinology at Stanford Medical Center",
        psychologicalTraits: "Detail-oriented, collaborative, High EQ",
        expertiseTags: [
            "Medical Review",
            "Clinical Data Validation",
            "Fair Balance Assessment",
            "Label Compliance Review",
            "Medical Claims Substantiation",
        ],
    },
    {
        name: "James Morrison",
        image: "/2.jpeg",
        badgeText: "AI",
        badgeClass: "bg-purple-300",
        badgeTextClass: "text-purple-900",
        roleColorClass: "bg-green-500",
        description:
            "As Senior Legal Director, I bring extensive experience in pharmaceutical advertising, promotion, and scientific communication review. My previous role at FDA's Office of Prescription Drug Promotion gave me unique insights into regulatory expectations. I handle both promotional and non-promotional material reviews, from advertising campaigns to clinical trial agreements and medical communications. I specialize in risk assessment across various communication types and have handled multiple FDA submissions. I particularly enjoy finding solutions that balance business needs with regulatory compliance.",
        age: "45",
        location: "East Hanover, NJ",
        income: "$265,000",
        profession: "Legal Director",
        experience: "15 years (5 FDA, 10 pharma)",
        educationalBackground:
            "JD from Columbia Law School, MPH in Healthcare Policy from Yale University",
        psychologicalTraits: "Methodical, assertive, composed",
        expertiseTags: [
            "Legal Compliance Review",
            "Promotional Claims Review",
            "Legal Reference Validation",
            "PI Compliance Check",
        ],
    },
    {
        name: "Patricia Thompson",
        image: "/3.jpeg",
        badgeText: "AI",
        badgeClass: "bg-purple-300",
        badgeTextClass: "text-purple-900",
        roleColorClass: "bg-gray-500",
        description:
            "I serve as Executive Director of Regulatory Affairs, bringing deep expertise in both promotional and non-promotional materials review. My six years at FDA's OPDP shaped my understanding of compliance requirements across all communication types. I hold RAC certification and lead global regulatory strategy for materials ranging from promotional campaigns to scientific exchange documents. I've managed regulatory submissions for multiple therapeutic areas and enjoy navigating complex global requirements for both commercial and medical communications.",
        age: "52",
        location: " Princeton, NJ",
        income: "$245,000",
        profession: "Exec. Director, Regulatory Affairs",
        experience: "25 years (6 FDA, 19 pharma)",
        educationalBackground:
            "PharmD from University of Michigan, MS in Regulatory Affairs from Northeastern University",
        psychologicalTraits: "Meticulous, diplomatic, level-headed",
        expertiseTags: [
            "Regulatory Review",
            "Global Regulatory Compliance",
            "FDA Submission Guidelines",
            "Promotional Guidance Verification",
        ],
    },
    {
        name: "Mike Rodriguez",
        image: "/4.jpeg",
        badgeText: "AI",
        badgeClass: "bg-purple-300",
        badgeTextClass: "text-purple-900",
        roleColorClass: "bg-cyan-500",
        description:
            "I'm a Senior Brand Operations Manager, managing complex review schedules for multiple brands across both promotional and non-promotional materials. With my MBA in pharmaceutical marketing, I've developed expertise in MLR process management and am proficient in various review systems. I coordinate reviews for everything from promotional campaigns to medical information letters and scientific communications. I lead training sessions for new reviewers and take pride in ensuring smooth cross-functional reviews while maintaining compliance standards.",
        age: "35",
        location: "New York City, NY",
        income: "$145,000",
        profession: "Senior Brand Operations Manager",
        experience: "7 years",
        educationalBackground:
            "MBA in Pharmaceutical Marketing from NYU Stern, BS in Business Administration from Boston University",
        psychologicalTraits: "Innovative, organized, outgoing",
        expertiseTags: [
            "Brand Strategy Alignment",
            "Brand Voice Consistency",
            "Brand Asset Management",
            "Cross-Channel Consistency",
        ],
    },
    {
        name: "Emily Parker",
        image: "/5.jpeg",
        badgeText: "AI",
        badgeClass: "bg-purple-300",
        badgeTextClass: "text-purple-900",
        roleColorClass: "bg-yellow-500",
        description:
            "As a Marketing Operations Specialist, I manage review processes for various materials including promotional campaigns, medical communications, and scientific materials. I'm certified in project management (PMP) and have spent 5 years dedicated to MLR coordination. I specialize in digital asset review workflows and compliance documentation across all communication types. I enjoy streamlining our review processes and ensuring efficient routing of materials through appropriate review channels. My strength lies in understanding different review requirements for promotional versus non-promotional materials.",
        age: "30",
        location: "Kenilworth, NJ",
        income: "$95,000",
        profession: "Marketing Operations Specialist",
        experience: "5 years",
        educationalBackground:
            "Bachelor's in Marketing and Communications from Rutgers, Project Management Professional Certification from PMI",
        psychologicalTraits: "Organized, diplomatic, innovative",
        expertiseTags: [
            "Marketing Material Review",
            "Digital Asset Management",
            "Social Media Guidelines",
            "Content Distribution Guidelines",
        ],
    },
    // Add more personas as needed
];

interface PersonaCardProps {
    persona: Persona;
    isSelected: boolean;
    onClick: () => void;
}

const PersonaCard: React.FC<PersonaCardProps> = ({
    persona,
    isSelected,
    onClick,
}) => {
    return (
        <div className="flex-none w-72 md:w-96 p-4" onClick={onClick}>
            <Card
                className={`w-64 md:w-80 h-90 md:h-80 p-2 md:p-3 pb-0 relative cursor-pointer transition-all duration-200 ${
                    isSelected
                        ? "ring-2 ring-blue-500 shadow-lg transform scale-105"
                        : "hover:shadow-md"
                }`}
            >
                {/* AI Badge */}
                <div className="absolute top-2 md:top-3 right-2 md:right-3">
                    <Badge
                        className={`${persona.badgeClass} w-8 md:w-9 h-7 md:h-8 flex items-center justify-center [clip-path:polygon(25%_0%,75%_0%,100%_50%,75%_100%,25%_100%,0%_50%)]`}
                    >
                        <span
                            className={`${persona.badgeTextClass} font-bold text-sm md:text-base`}
                        >
                            {persona.badgeText}
                        </span>
                    </Badge>
                </div>

                <CardContent className="p-0">
                    {/* Profile Image */}
                    <div className="flex justify-center mb-3 md:mb-4">
                        <Avatar className="w-20 md:w-28 h-20 md:h-28">
                            <AvatarImage
                                src={persona.image}
                                alt={`${persona.name} profile`}
                                className="object-cover"
                            />
                            <AvatarFallback>{persona.name[0]}</AvatarFallback>
                        </Avatar>
                    </div>

                    {/* Name */}
                    <h2 className="text-lg md:text-xl font-medium text-center mb-3 md:mb-4">
                        {persona.name}
                    </h2>

                    {/* Content Placeholder Bars */}
                    <div className="space-y-2 md:space-y-3 mb-3 md:mb-4">
                        <Skeleton className="h-2 md:h-3 w-full" />
                        <Skeleton className="h-2 md:h-3 w-full" />
                        <Skeleton className="h-2 md:h-3 w-4/5" />
                        <Skeleton className="h-2 md:h-3 w-2/5" />
                    </div>

                    {/* Role Badge */}
                    <div className="flex justify-center">
                        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2">
                            <Badge
                                className={`${persona.roleColorClass} h-8 md:h-10 px-4 md:px-6 text-sm md:text-base font-medium rounded-full flex items-center justify-center whitespace-nowrap`}
                            >
                                {persona.profession}
                            </Badge>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

const PersonaPopup: React.FC<{ persona: Persona; onClose: () => void }> = ({
    persona,
    onClose,
}) => {
    const navigate = useNavigate();

    const handleChatClick = () => {
        navigate("/chat-with-persona", {
            state: {
                persona: {
                    name: persona.name,
                    image: persona.image,
                    profession: persona.profession,
                },
            },
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white w-11/12 md:w-3/4 lg:w-2/3 xl:w-1/3 p-6 rounded-lg shadow-lg relative">
                <div className="flex justify-end">
                    <button
                        onClick={onClose}
                        className="text-gray-600 hover:text-black"
                    >
                        &times;
                    </button>
                </div>

                {/* Header with Avatar and Name */}
                <div className="flex flex-col items-center mb-6">
                    <div className="relative">
                        <Avatar className="w-24 h-24 mb-4">
                            <AvatarImage
                                src={persona.image}
                                alt={`${persona.name} profile`}
                                className="object-cover"
                            />
                            <AvatarFallback>{persona.name[0]}</AvatarFallback>
                        </Avatar>
                        <Badge
                            className={`${persona.badgeClass} w-8 md:w-9 h-7 md:h-8 flex items-center justify-center [clip-path:polygon(25%_0%,75%_0%,100%_50%,75%_100%,25%_100%,0%_50%)] absolute -right-4 top-0`}
                        >
                            <span
                                className={`${persona.badgeTextClass} font-bold text-sm md:text-base`}
                            >
                                {persona.badgeText}
                            </span>
                        </Badge>
                    </div>
                    <h2 className="text-2xl font-bold">{persona.name}</h2>
                    {/* Expertise Tags */}
                    <div className="flex flex-wrap gap-2 justify-center mt-3">
                        {persona.expertiseTags.map((tag, index) => {
                            const colors = [
                                "bg-blue-100 text-blue-800 hover:bg-blue-200",
                                "bg-green-100 text-green-800 hover:bg-green-200",
                                "bg-purple-100 text-purple-800 hover:bg-purple-200",
                                "bg-pink-100 text-pink-800 hover:bg-pink-200",
                                "bg-yellow-100 text-yellow-800 hover:bg-yellow-200",
                                "bg-indigo-100 text-indigo-800 hover:bg-indigo-200",
                                "bg-red-100 text-red-800 hover:bg-red-200",
                                "bg-teal-100 text-teal-800 hover:bg-teal-200",
                            ];

                            return (
                                <Badge
                                    key={index}
                                    className={colors[index % colors.length]}
                                >
                                    {tag}
                                </Badge>
                            );
                        })}
                    </div>
                </div>

                {/* Two Column Layout */}
                <div className="flex flex-col md:flex-row gap-8">
                    {/* Left Column - About Me */}
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold mb-3">About Me</h3>
                        <div className="bg-gray-100 p-4 rounded-md relative">
                            <span className="absolute left-2 top-0 text-5a55f4 text-3xl font-bold italic">
                                "
                            </span>
                            <p className="text-gray-700 px-4 italic">
                                {persona.description}
                            </p>
                            <span className="absolute right-2 bottom-0 text-5a55f4 text-3xl font-bold italic">
                                "
                            </span>
                        </div>
                        <div className="pt-4">
                            <button
                                onClick={handleChatClick}
                                className="w-full bg-blue-500 text-white py-3 px-6 rounded-md hover:bg-blue-600 transition-colors"
                            >
                                Chat with{" "}
                                {(() => {
                                    const nameParts = persona.name.split(" ");
                                    return nameParts[0].length < 4
                                        ? `${nameParts[0]} ${nameParts[1]}`
                                        : nameParts[0];
                                })()}
                            </button>
                        </div>
                    </div>

                    {/* Right Column - Details */}
                    <div className="flex-1">
                        <div className="space-y-6">
                            <div>
                                <h3 className="text-lg font-semibold mb-3">
                                    Demographics
                                </h3>
                                <ul className="text-gray-700 space-y-2 bg-gray-100 p-4 rounded-md">
                                    <li>
                                        <strong>Age:</strong> {persona.age}
                                    </li>
                                    <li>
                                        <strong>Location:</strong>{" "}
                                        {persona.location}
                                    </li>
                                    <li>
                                        <strong>Profession:</strong>{" "}
                                        {persona.profession}
                                    </li>
                                    <li>
                                        <strong>Experience:</strong>{" "}
                                        {persona.experience}
                                    </li>
                                </ul>
                            </div>

                            <div>
                                <h3 className="text-lg font-semibold mb-2">
                                    Educational Background
                                </h3>
                                <p className="text-gray-700 bg-gray-100 p-4 rounded-md">
                                    {persona.educationalBackground}
                                </p>
                            </div>

                            <div>
                                <h3 className="text-lg font-semibold mb-2">
                                    Psychological Traits
                                </h3>
                                <p className="text-gray-700 bg-gray-100 p-4 rounded-md">
                                    {persona.psychologicalTraits}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const PersonaList: React.FC = () => {
    const [selectedPersona, setSelectedPersona] = useState<Persona | null>(
        null
    );

    return (
        <div className="flex flex-col w-full h-full">
            <div className="w-full bg-gray-50 p-4 md:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center max-w-7xl mx-auto">
                    {personas.map((persona, index) => (
                        <PersonaCard
                            key={index}
                            persona={persona}
                            isSelected={selectedPersona?.name === persona.name}
                            onClick={() => setSelectedPersona(persona)}
                        />
                    ))}
                    {/* Add New Persona Card */}
                    <div className="flex-none w-72 md:w-96 p-4">
                        <Card className="w-64 md:w-80 h-90 md:h-80 p-2 md:p-3 pb-0 relative cursor-pointer hover:shadow-md">
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <div className="text-gray-400 text-center">
                                    <div className="text-6xl mb-2">+</div>
                                    <div className="text-lg">
                                        Add New Persona
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </div>

            {selectedPersona && (
                <PersonaPopup
                    persona={selectedPersona}
                    onClose={() => setSelectedPersona(null)}
                />
            )}
        </div>
    );
};

export default PersonaList;
